/**
 * Log in — the password, and for a person with two-factor on, the second step.
 *
 * Visual Reference: `design/screens/auth-login.html`, ten states. Its notes are
 * the specification, not decoration:
 *
 *   · "one message above the form, never at field level. It doesn't say which
 *     half was wrong, and it offers the reset link inline instead of making the
 *     reader hunt for it."
 *   · locked is "amber, not red: nothing has broken and nothing is lost. It
 *     names the wait in minutes, and the button is disabled rather than removed
 *     so the form doesn't shift when the lock lifts."
 *   · the code step "replaces the form, not the page … keeps the recovery route
 *     above the field, where a stuck person already looks, and names the
 *     account, so a shared computer surprises nobody."
 *   · expired: "the person is back at their password with their email kept and
 *     one amber line saying why."
 *
 * Every sentence comes from the API (`services/auth/errors.ts`), so the screen
 * renders what the server said rather than a second copy that can drift.
 * DECISION-035 sets the lock at five failures and five minutes, and since Story
 * 24.2 a wrong code counts against the same lock as a wrong password.
 *
 * THE SECOND STEP (Story 24.2, DECISION-156). With two-factor on, the password
 * step answers `{ twoFactorRedirect: true }` and signs nobody in — the session
 * it made is deleted and a challenge cookie left in its place. Until this story
 * the screen ignored that body and sent the person round to `/login` for ever.
 * Now it asks for the code, or a recovery code, and posts it to the plugin's
 * two verify endpoints, which finish the sign-in.
 *
 * Stories 3.4 and 24.2.
 */
import { useState, type FormEvent, type ReactNode } from 'react'
import { AuthAction, AuthFootnote, AuthHeading, AuthLink, AuthPanel } from './AuthPanel'
import { Button } from '../ui/Button'
import { Callout } from '../ui/Callout'
import { Checkbox } from '../ui/Checkbox'
import { Field } from '../ui/Field'
import { Wordmark } from '../ui/Wordmark'
import { apiPost, ApiFailure } from '../lib/api'

type Step = 'password' | 'code' | 'recovery'

/** The recovery route the server's wrong-code sentence ends with (`SIGN_IN_CHALLENGE`). */
const RECOVERY_ROUTE = 'use a recovery code instead'

export function LogIn() {
  const [step, setStep] = useState<Step>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  /** The amber line the password step shows after a code step outlived itself. */
  const [expired, setExpired] = useState<string | null>(null)

  // A 429 on this surface is the lockout, and it is a WAIT rather than a
  // failure — the tone follows the mockup's own reasoning. The code step meets
  // the same lock, so the same test serves both.
  const locked = failure?.code === 'rate_limited'

  async function submitPassword(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setFailure(null)
    setExpired(null)
    try {
      const result = await apiPost<{ twoFactorRedirect?: boolean } | null>('/v1/auth/sign-in/email', {
        email,
        password,
        rememberMe,
      })
      if (result?.twoFactorRedirect === true) {
        // The password was right and nobody is signed in yet: ask for the code.
        setPassword('')
        setCode('')
        setStep('code')
        return
      }
      window.location.assign('/')
    } catch (error) {
      setFailure(error instanceof ApiFailure ? error : null)
    } finally {
      setSubmitting(false)
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setFailure(null)
    try {
      await apiPost(
        step === 'code' ? '/v1/auth/two-factor/verify-totp' : '/v1/auth/two-factor/verify-backup-code',
        { code: code.trim() },
      )
      window.location.assign('/')
    } catch (error) {
      const refused = error instanceof ApiFailure ? error : null
      if (refused?.code === 'session_expired') {
        // The challenge outlived its ten minutes: back to the password, with the
        // address kept and the server's amber line saying why.
        setStep('password')
        setPassword('')
        setCode('')
        setExpired(refused.message)
        return
      }
      setFailure(refused)
    } finally {
      setSubmitting(false)
    }
  }

  /** Between the code and a recovery code. A lock outlives the switch — it is the account's. */
  function switchTo(next: Exclude<Step, 'password'>) {
    setStep(next)
    setCode('')
    if (!locked) setFailure(null)
  }

  /** "Use a different account" — an empty password step, as if freshly loaded. */
  function startOver() {
    setStep('password')
    setEmail('')
    setPassword('')
    setCode('')
    setFailure(null)
    setExpired(null)
  }

  return (
    <AuthPanel
      wordmark={false}
      stack="gap-mdx-24"
      aside={
        <>
          <Wordmark />
          <span className="text-mdx-25 font-mdx-bold leading-mdx-heading tracking-mdx-h1">
            Welcome back.
          </span>
        </>
      }
    >
      {step === 'password' ? (
        <>
          <AuthHeading title="Log in" />

          <form className="flex flex-col gap-mdx-20" onSubmit={submitPassword} noValidate>
            {failure ? (
              <Callout tone={locked ? 'notice' : 'error'}>
                {failure.message}
                {!locked ? (
                  <>
                    {' '}
                    <AuthLink href="/reset">Reset your password</AuthLink>
                  </>
                ) : null}
              </Callout>
            ) : expired ? (
              <Callout tone="notice">{expired}</Callout>
            ) : null}

            <Field
              label="Email"
              type="email"
              name="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Field
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              action={
                <a
                  href="/reset"
                  className="rounded-mdx-sm text-mdx-13 font-mdx-semibold text-mdx-brand-deep underline"
                >
                  Forgot your password?
                </a>
              }
            />

            <Checkbox
              label="Keep me logged in"
              name="keep"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />

            <Button
              type="submit"
              full
              loading={submitting ? 'Logging you in' : undefined}
              // Disabled rather than removed, so the form does not shift when the
              // lock lifts. The title carries the wait for a pointer user.
              disabled={locked}
              title={locked ? failure?.message : undefined}
            >
              Log in
            </Button>

            <AuthFootnote>
              New to Leadgible? <AuthLink href="/signup">Create an account</AuthLink>
            </AuthFootnote>
          </form>
        </>
      ) : (
        <>
          {step === 'code' ? (
            <AuthHeading
              title="Enter your code"
              note="Two-factor authentication is on for this account. Open the authenticator app on your phone and type the code it shows for Leadgible."
            />
          ) : (
            <AuthHeading
              title="Enter a recovery code"
              note="Recovery codes are the ten codes you saved when you turned on two-factor authentication. Each one works once."
            />
          )}

          <form className="flex flex-col gap-mdx-20" onSubmit={submitCode} noValidate>
            {failure ? (
              <Callout tone={locked ? 'notice' : 'error'}>
                {step === 'code' ? withRecoveryRoute(failure.message, () => switchTo('recovery')) : failure.message}
              </Callout>
            ) : null}

            {step === 'code' ? (
              <Field
                key="code"
                label="Six-digit code"
                name="code"
                value={code}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                /**
                 * NO `maxLength`, as in Profile's dialogs: the attribute clips the
                 * raw value before the strip below runs, so pasting "123 456" —
                 * how every authenticator app displays it — would arrive as "123".
                 * Strip, then take six.
                 */
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                action={
                  <AuthAction small onClick={() => switchTo('recovery')}>
                    Use a recovery code instead
                  </AuthAction>
                }
              />
            ) : (
              <Field
                key="recovery"
                label="Recovery code"
                name="recovery-code"
                value={code}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                autoFocus
                onChange={(e) => setCode(e.target.value)}
                action={
                  <AuthAction small onClick={() => switchTo('code')}>
                    Use your authenticator app instead
                  </AuthAction>
                }
              />
            )}

            <Button
              type="submit"
              full
              loading={submitting ? 'Logging you in' : undefined}
              disabled={locked}
              title={locked ? failure?.message : undefined}
            >
              Log in
            </Button>

            <AuthFootnote>
              <span className="break-words">Not {email}?</span>{' '}
              <AuthAction onClick={startOver}>Use a different account</AuthAction>
            </AuthFootnote>
          </form>
        </>
      )}
    </AuthPanel>
  )
}

/**
 * The server's wrong-code sentence, with its recovery route made the action it
 * names — `auth-login.html`'s `code-error` offers it inline, "instead of making
 * the reader hunt for it", as the password error offers the reset. The words are
 * the server's; the screen only makes the last of them pressable, and a
 * sentence without them renders as it came.
 */
function withRecoveryRoute(message: string, onRecovery: () => void): ReactNode {
  const at = message.indexOf(RECOVERY_ROUTE)
  if (at === -1) return message
  return (
    <>
      {message.slice(0, at)}
      <AuthAction onClick={onRecovery}>{RECOVERY_ROUTE}</AuthAction>
      {message.slice(at + RECOVERY_ROUTE.length)}
    </>
  )
}
